import { createContext, useCallback, useId, useMemo } from "react";
import MuiDialog from "@mui/material/Dialog";
import type { DialogProps as MuiDialogProps } from "@mui/material/Dialog";
import useDefinedContext from "hooks/useDefinedContext";

const dialogSizing = {
  sm: { maxWidth: "300px" },
  md: { maxWidth: "600px" },
  lg: { maxWidth: "1000px" },
  full: { width: "100%", height: "100%", maxWidth: "100%" },
};

interface DialogContextProps {
  open: boolean;
  size: keyof typeof dialogSizing;
  onClose: (event: React.MouseEvent, reason?: "backdropClick" | "escapeKeyDown") => void;
  ariaLabelledby: string;
  ariaDescribedby: string;
  fullScreen?: boolean;
}

const DialogContext = createContext<DialogContextProps | undefined>(undefined);

/**
 * @important
 * This hook should only be used inside the Dialog component.
 */
export function useDialogContext() {
  return useDefinedContext(DialogContext);
}

interface DialogProps extends Omit<MuiDialogProps, "maxWidth" | "minWidth"> {
  open: boolean;
  /**
   * Set the size of the dialog.
   * The size will determine the width of the dialog and how the header and close button will be positioned.
   * @default "sm"
   */
  size?: keyof typeof dialogSizing;
  onClose?: (event: React.MouseEvent, reason?: "backdropClick" | "escapeKeyDown") => void;
  /**
   * Set the maximum width of the dialog
   * if not provided, the dialog will use the default width for the size.
   */
  width?: number;
  /**
   * Similar to `disableEscapeKeyDown` but for the backdrop.
   * If true, clicking outside will not fire the onClose callback.
   * @default false
   */
  disableBackdropClose?: boolean;
}

/**
 * Dialog component that provides a context to the children components.
 *
 * This is a composable component that allows you to create custom dialogs with ease.
 * The meaning behind the composability is that you can create a dialog with a custom header, content, and actions
 * without having to worry about fitting all the pieces together.
 *
 * If we want to create a dialog with custom sizes and positions we can easily use new pieces of the dialog
 * and compose them together to create a new one.
 */
function Dialog(props: DialogProps) {
  const {
    onClose,
    size = "sm",
    width,
    fullScreen,
    closeAfterTransition = false,
    slotProps,
    disableBackdropClose = false,
    ...rest
  } = props;

  const handleClose = useCallback(
    (event: React.MouseEvent, reason?: "backdropClick" | "escapeKeyDown") => {
      if (reason === "backdropClick") {
        if (disableBackdropClose) {
          return;
        }
      }
      onClose?.(event, reason);
    },
    [disableBackdropClose, onClose]
  );

  const sizing = useMemo(() => {
    if (fullScreen) return;
    const dialogSize = dialogSizing[size];
    return { maxWidth: width ?? dialogSize.maxWidth };
  }, [fullScreen, width, size]);

  const ariaLabelledby = useId();
  const ariaDescribedby = `${ariaLabelledby}-description`;

  const value = useMemo<DialogContextProps>(
    () => ({ open: props.open, size: size, onClose: handleClose, ariaLabelledby, ariaDescribedby, fullScreen }),
    [ariaDescribedby, ariaLabelledby, fullScreen, handleClose, props.open, size]
  );

  const memoSlotProps = useMemo(() => {
    return {
      ...slotProps,
      paper: (ownerState) => {
        const baseProps =
          typeof slotProps?.paper === "function" ? slotProps.paper(ownerState) : (slotProps?.paper ?? {});

        return {
          ...baseProps,
          sx: {
            borderRadius: fullScreen ? undefined : "20px",
            position: "relative",
            width: "100%",
            ...sizing,
            ...baseProps?.sx,
          },
        };
      },
    } as MuiDialogProps["slotProps"];
  }, [fullScreen, sizing, slotProps]);

  return (
    <DialogContext.Provider value={value}>
      <MuiDialog
        aria-labelledby={ariaLabelledby}
        aria-describedby={ariaDescribedby}
        onClose={handleClose}
        closeAfterTransition={closeAfterTransition}
        scroll="paper" // allow the dialog to scroll if the content is too big
        fullScreen={fullScreen}
        slotProps={memoSlotProps}
        {...rest}
        maxWidth={false}
      />
    </DialogContext.Provider>
  );
}

export default Dialog;
